// src/components/patient/NotesViewerModal.js
//
// A large, read-only pop-up for reading a patient's notes (Legacy Notes and the
// Coordinator Notes timeline on the patient page, 2026-10-26). Display only: it never
// edits or saves anything. Rendered into <body> so no card can clip it. Own classes
// (notes-viewer-*) in both light and dark mode.

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export default function NotesViewerModal({ open, title, subtitle = "", onClose, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-4xl max-h-[88vh] flex flex-col rounded-2xl border shadow-2xl notes-viewer-modal"
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b notes-viewer-divider">
          <div className="min-w-0">
            <h2 className="text-base font-semibold notes-viewer-title">{title}</h2>
            {subtitle && <p className="text-xs mt-0.5 notes-viewer-muted">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition notes-viewer-close"
          >
            <X className="w-3.5 h-3.5" />
            Close
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 notes-viewer-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
